import React, { FC, useCallback, useEffect, useState } from "react";
import {
    Box,
    Checkbox,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingPlanEndpoint, pricingPlansEndpoint } from "../../../api/endpoints";
import { usePricing } from "./PricingContext";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { INTERVAL_LABELS, PDP_SCORE_INTERVALS } from "../../../utils/plans";
import type { PlanFeatureRow, PlanFeatureValue, PricingPlan } from "../../../types/pricingPlans";
import { parseNumberInput } from "./inlinePatch";

// Brand subscription plans: the internal /pricing table's columns and the
// defaults a brand receives when put on the plan. Keys are fixed by the
// API; everything else edits inline (blur to save). Changing a default
// here does NOT touch brands already on the plan. "On /pricing" = the plan
// is a column; the three enterprise plans share the Enterprise Brand
// column, so Enterprise RMN / Agency stay unticked.
export const PlansTab: FC = () => {
    const { canEdit } = usePricing();
    const [plans, setPlans] = useState<PricingPlan[] | null>(null);
    const [featureRows, setFeatureRows] = useState<PlanFeatureRow[]>([]);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axiosInstant.get(pricingPlansEndpoint);
            setPlans(data.plans || []);
            setFeatureRows(data.featureRows || []);
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to load plans");
            setPlans([]);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!plans) return <DotLottleLoader />;

    const handlePatch = async (key: string, patch: Partial<PricingPlan> | Record<string, unknown>) => {
        setBusyKey(key);
        try {
            await axiosInstant.patch(pricingPlanEndpoint(key), patch);
            toast.success("Saved");
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyKey(null);
        }
    };

    const patchNumber = (
        plan: PricingPlan,
        field: "monthly_price" | "default_max_products" | "sort_order",
        raw: string,
        opts: { nullable?: boolean; integer?: boolean },
        input?: HTMLInputElement | HTMLTextAreaElement | null
    ) => {
        const parsed = parseNumberInput(raw, plan[field], opts, input);
        if ("skip" in parsed) return;
        handlePatch(plan.key, { [field]: parsed.value });
    };

    const patchFeature = (plan: PricingPlan, rowKey: string, value: PlanFeatureValue) => {
        handlePatch(plan.key, { features: { ...(plan.features || {}), [rowKey]: value } });
    };

    const matrixRows = featureRows.filter((r) => !r.virtual);
    const disabled = (key: string) => !canEdit || busyKey === key;

    return (
        <Box className="flex flex-col gap-4">
            <Box className="rounded-lg p-3 bg-tint text-[13px] text-text-1">
                Plans drive the internal <b>/pricing</b> comparison table and the defaults applied to a brand when its
                plan is set (Brand &gt; Details). Blank price = &ldquo;Contact sales&rdquo;; blank product limit = unlimited.
                The three analysis intervals are the fastest cadence a brand on the plan may run each analysis at. Feature labels
                are client-visible copy. <b>On /pricing</b> makes a plan a column: the Enterprise Brand row is the single
                &ldquo;Enterprise&rdquo; column, so Enterprise RMN / Agency stay off the table (their plans still apply
                to brands).
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid var(--border)" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell sx={{ minWidth: 180 }}>Display name</TableCell>
                            <TableCell sx={{ minWidth: 240 }}>Description</TableCell>
                            <TableCell>Monthly price</TableCell>
                            <TableCell>Default product limit</TableCell>
                            <TableCell>PDP analysis interval</TableCell>
                            <TableCell>Consumer Voice interval</TableCell>
                            <TableCell>AI Presence interval</TableCell>
                            <TableCell align="center">On /pricing</TableCell>
                            <TableCell align="center">Active</TableCell>
                            <TableCell>Sort</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {plans.map((p) => (
                            <TableRow key={p.key}>
                                <TableCell>
                                    <span className="font-mono text-[12px]">{p.key}</span>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        defaultValue={p.display_name}
                                        disabled={disabled(p.key)}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v && v !== p.display_name) handlePatch(p.key, { display_name: v });
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        defaultValue={p.description || ""}
                                        disabled={disabled(p.key)}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== (p.description || "")) handlePatch(p.key, { description: v || null });
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, step: 1 }}
                                        defaultValue={p.monthly_price === null ? "" : p.monthly_price}
                                        disabled={disabled(p.key)}
                                        helperText={p.monthly_price === null ? "Contact sales" : formatCurrency(p.monthly_price)}
                                        onBlur={(e) => patchNumber(p, "monthly_price", e.target.value, { nullable: true }, e.target)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, step: 1 }}
                                        defaultValue={p.default_max_products === null ? "" : p.default_max_products}
                                        disabled={disabled(p.key)}
                                        helperText={p.default_max_products === null ? "Unlimited" : " "}
                                        onBlur={(e) =>
                                            patchNumber(p, "default_max_products", e.target.value, { nullable: true, integer: true }, e.target)
                                        }
                                    />
                                </TableCell>
                                {(["max_pdp_score_interval", "max_cv_analysis_interval", "max_aip_analysis_interval"] as const).map((col) => (
                                    <TableCell key={col}>
                                        <Select
                                            size="small"
                                            value={p[col] || "monthly"}
                                            disabled={disabled(p.key)}
                                            onChange={(e) => handlePatch(p.key, { [col]: e.target.value })}
                                        >
                                            {PDP_SCORE_INTERVALS.map((i) => (
                                                <MenuItem key={i} value={i}>
                                                    {INTERVAL_LABELS[i]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                ))}
                                <TableCell align="center">
                                    <Checkbox
                                        checked={p.public !== false}
                                        disabled={disabled(p.key)}
                                        onChange={(e) => handlePatch(p.key, { public: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        checked={p.active !== false}
                                        disabled={disabled(p.key)}
                                        onChange={(e) => handlePatch(p.key, { active: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        sx={{ width: 70 }}
                                        defaultValue={p.sort_order}
                                        disabled={disabled(p.key)}
                                        onBlur={(e) => patchNumber(p, "sort_order", e.target.value, { integer: true }, e.target)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Feature matrix (rows on the /pricing table)
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid var(--border)" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 240 }}>Feature</TableCell>
                            {plans.map((p) => (
                                <TableCell key={p.key} align="center">
                                    {p.display_name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {matrixRows.map((row) => (
                            <TableRow key={row.key}>
                                <TableCell sx={row.parent ? { pl: 5 } : undefined}>
                                    <div className={row.parent ? "" : "font-medium"}>
                                        {row.parent ? "↳ " : ""}
                                        {row.label}
                                    </div>
                                    {row.group && !row.parent && <div className="text-[11px] text-text-2">{row.group}</div>}
                                </TableCell>
                                {plans.map((p) => {
                                    const v = p.features ? p.features[row.key] : undefined;
                                    if (row.type === "bool") {
                                        return (
                                            <TableCell key={p.key} align="center">
                                                <Checkbox
                                                    checked={v === true}
                                                    disabled={disabled(p.key)}
                                                    onChange={(e) => patchFeature(p, row.key, e.target.checked)}
                                                />
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell key={p.key} align="center">
                                            <TextField
                                                size="small"
                                                type={row.type === "number" ? "number" : "text"}
                                                defaultValue={v === null || v === undefined ? "" : String(v)}
                                                disabled={disabled(p.key)}
                                                onBlur={(e) => {
                                                    const raw = e.target.value.trim();
                                                    const next: PlanFeatureValue =
                                                        raw === "" ? null : row.type === "number" ? Number(raw) : raw;
                                                    if (next !== (v === undefined ? null : v)) patchFeature(p, row.key, next);
                                                }}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
